import { DataHeader, DataValue, UnderlyingDataTable } from '@tableau/api-internal-contract-js';
import * as Enums from '../../gen-src/preslayer/Enums';
import { DataDictionaryPresModel, ShowDataTableColumnPresModel, ShowDataTablePresModel } from '../../gen-src/preslayer/PresModels';
import { PresLayerToApiEnumMappings as EnumMapping } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';
import { UnderlyingDataConverter } from './UnderlyingDataConverter';

/**
 * Contains static helper methods for converting from a ShowDataTablePresModel into the
 * api representation (to support Object Model changes to the platform).
 * For more info: https://mytableau.tableaucorp.com/pages/viewpage.action?spaceKey=devdata&title=API+Changes+for+Object+Model
 * Most of the code is a direct port from ApiShowDataHandler.cs
 * https://opengrok.prod.tableautools.com/source/xref/teams_near/workgroup/vqlweb/scriptsharp/src/ApiDomain/ApiShowDataHandler.cs
 */

export enum DataScope {
  Summary,
  Underlying,
}

export function parseShowDataTablePresModel(dataScope: DataScope, tablePM: ShowDataTablePresModel): UnderlyingDataTable {
  // In version 2.0 of ShowDataTablePresModel, we have a DataDictionaryPresModel
  // The ShowDataTableColumn includes two indices: FormattedValueIndex, and ValueIndex into the DataDictionaryPresModel

  const dataDictionary: DataDictionaryPresModel = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(tablePM.dataDictionary);

  const columns = tablePM.showDataTableColumnPresModels;
  const apiColumns = buildDataHeaders(columns, tablePM.referencedColumns);

  const dataTable = buildDataTable(dataDictionary, columns);

  return buildUnderlyingDataTable(dataTable, apiColumns, dataScope);
}

/**
 * The Object Model can return an empty presentation model if there is no data. (For example it is all filtered out.)
 * makeEmptyDataTable supports a default return value so that we don't have to handle special cases in api-external-js.
 * @param dataScope is either Underlying or Summary
 */
export function makeEmptyDataTable(dataScope: DataScope): UnderlyingDataTable {
  return buildUnderlyingDataTable([], [], dataScope);
}

export function buildDataHeaders(columns: ShowDataTableColumnPresModel[], referencedColumns: Array<string>): Array<DataHeader> {
  const apiColumns = new Array<DataHeader>();

  if (!isNull(columns)) {
    for (let index = 0; index < columns.length; index++) {
      apiColumns.push(buildHeader(columns[index], referencedColumns, index));
    }
  }

  return apiColumns;
}

function buildHeader(column: ShowDataTableColumnPresModel, referencedColumns: string[], index: number): DataHeader {
  return {
    dataType: EnumMapping.dataType.convert(column.dataType),
    fieldName: column.uniqueName,
    fieldCaption: column.fieldCaption,
    isReferenced: referencedColumns.indexOf(column.uniqueName) >= 0,
    index: index,
  };
}

function buildUnderlyingDataTable(dataTable: DataValue[][], apiColumns: DataHeader[], dataScope: DataScope): UnderlyingDataTable {
  return {
    data: {
      dataTable: dataTable,
      headers: apiColumns,
    },
    isSummary: dataScope === DataScope.Summary,
  };
}

export function buildDataTable(dataDictionary: DataDictionaryPresModel, columns: ShowDataTableColumnPresModel[]): Array<Array<DataValue>> {
  if (columns.length === 0) {
    return new Array<Array<DataValue>>();
  }

  // The client can request only values, or only formatted values, so we need to allow one to be empty
  const rowCount = Math.max(columns[0].valueIndices.length, columns[0].formatValIdxs.length);
  const columnCount = columns.length;

  const includeNativeValues: boolean = columns[0].valueIndices.length > 0;
  const includeFormattedValues: boolean = columns[0].formatValIdxs.length > 0;

  const newTable = new Array<Array<DataValue>>(rowCount);

  // Initialize all of our rows
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    newTable[rowIndex] = new Array<DataValue>(columnCount);
  }

  // Go through column-by-column and convert the values
  for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
    const column = columns[columnIndex];
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      const formattedValue = includeFormattedValues
        ? UnderlyingDataConverter.lookupValueFromDictionary(dataDictionary, Enums.DataType.String, column.formatValIdxs[rowIndex])
        : undefined;

      const value: DataValue = {
        value: includeNativeValues
          ? UnderlyingDataConverter.lookupValueFromDictionary(dataDictionary, column.dataType, column.valueIndices[rowIndex])
          : undefined,
        formattedValue: formattedValue,
        aliasedValue: formattedValue,
      };

      newTable[rowIndex][columnIndex] = value;
    }
  }

  return newTable;
}

function isNull(test: Object): boolean {
  // tslint:disable-next-line:triple-equals
  return test == null; // == instead of === to check for both null and undefined.
}
